import type { Movement } from '@studio/models/ptz';
import { clampMagnitude, combine, Movements, scale } from '@studio/models/ptz';
import type { Handler } from './types.js';

/** @internal */
export class Choreographer {
  /** Handler callback */
  readonly #handler: Handler;

  /** Movement vectors */
  readonly #movements = new Map<Movement, number>();

  /**
   * Manages movement intent.
   *
   * @param handler - The function to be called in response to movement.
   */
  public constructor(handler: Handler) {
    this.#handler = handler;
  }

  /**
   * Add the specified movement to the choreography.
   *
   * @param movement - Camera movement to be added.
   * @param factor - Multiplication factor from `0..1` (default `1`).
   */
  public add(movement: Movement, factor: number = 1): void {
    this.#movements.set(movement, clampMagnitude(Math.abs(factor)));

    this.#dispatch();
  }

  /**
   * Remove the specified movement from the choreography.
   *
   * @param movement - Camera movement to be removed.
   */
  public remove(movement: Movement): void {
    this.#movements.delete(movement);

    this.#dispatch();
  }

  /** Stop all motion. */
  public stop(): void {
    // Clear all movements.
    this.#movements.clear();

    this.#dispatch();
  }

  /** Dispatch movement. */
  #dispatch(): void {
    // Compute all movements.
    const movements = [...this.#movements.entries()].map(([movement, factor]) =>
      scale(Movements[movement].ptz, factor)
    );

    // Trigger handler
    this.#handler(combine(...movements));
  }
}
